import { useState } from "react";
import type { CurrentBatter } from "./creaseBatters";
import { Avatar, Empty, PlayerLink } from "./parts";
import { RUN_COLOR, RUN_LABEL, isLeftHander, isShotBall, runKey, shotLabel } from "./shotData";
import type { RunKey } from "./shotData";
import { ShotField } from "./shotMap";
import type { LiveBall } from "./useLiveStats";

type Props = { batters: CurrentBatter[]; balls: LiveBall[]; live: boolean; loading?: boolean; error?: boolean };

const Card = ({ b, all, mode, live }: { b: CurrentBatter; all: LiveBall[]; mode: "spike" | "zone"; live: boolean }) => {
  const [flt, setFlt] = useState<RunKey>("all");
  const [vs, setVs] = useState("");
  const mine = all.filter((x) => x.batsmanId === b.id);
  const left = isLeftHander(mine.find((x) => x.batsmanBattingStyle) ?? { batsmanBattingStyle: null });
  const bowlers = [...new Set(mine.map((x) => x.bowler).filter(Boolean))];
  const scoped = vs ? mine.filter((x) => x.bowler === vs) : mine;
  const shots = scoped.filter(isShotBall);
  const shown = flt === "all" ? shots : shots.filter((x) => runKey(x.runs) === flt);
  const count = (k: RunKey) => (k === "all" ? shots.length : shots.filter((x) => runKey(x.runs) === k).length);
  const runsShown = shown.reduce((a, x) => a + x.runs, 0);

  const byShot = new Map<string, { r: number; f: number; s: number }>();
  shots.forEach((x) => {
    const key = shotLabel(x.shotType);
    if (!key) return;
    const e = byShot.get(key) ?? { r: 0, f: 0, s: 0 };
    e.r += x.runs;
    if (x.runs === 4) e.f++;
    if (x.runs === 6) e.s++;
    byShot.set(key, e);
  });
  const best = [...byShot.entries()].sort((p, q) => q[1].r - p[1].r)[0];
  const faced = scoped.filter((x) => x.legal);
  const dots = faced.filter((x) => x.runs === 0 && x.extraRuns === 0).length;
  const dotPct = faced.length ? Math.round((dots / faced.length) * 100) : null;

  return (
    <div className="cb">
      <div className="cb-h">
        <Avatar name={b.name} team={b.team} />
        <div>
          <div className="cb-n">
            <PlayerLink id={b.id} name={b.name} />
            <span className={`crole ${b.role === "STRIKER" ? "s" : "n"}`}>{b.role}</span>
          </div>
          <div className="cb-s">
            <b>{b.runs}</b>
            {!b.out && live ? "*" : ""} runs ({b.balls}) · {b.fours} four{b.fours === 1 ? "" : "s"} · {b.sixes} six{b.sixes === 1 ? "" : "es"}
          </div>
          {bowlers.length > 1 ? (
            <label className="cb-vs">
              vs
              <select
                aria-label="Filter by bowler"
                value={vs}
                onChange={(e) => {
                  setVs(e.target.value);
                  setFlt("all");
                }}
              >
                <option value="">All bowlers</option>
                {bowlers.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </div>
      </div>
      <div className="cb-b">
        <div className="cb-i">
          <div className="k">Productive shot</div>
          <b>{best ? best[0] : "–"}</b>
          <span>{best ? `${best[1].r} runs · ${best[1].f} four${best[1].f === 1 ? "" : "s"} · ${best[1].s} six${best[1].s === 1 ? "" : "es"}` : shots.length ? "Shot type not recorded" : "No scoring shots yet"}</span>
          <div className="k k2">Dot balls</div>
          <b className="cv">{dotPct === null ? "–" : `${dotPct}%`}</b>
          <div className="cbar">
            <i style={{ width: `${dotPct ?? 0}%` }} />
          </div>
        </div>
        <div className="cb-g">
          <ShotField balls={shown} left={left} mode={mode} label={b.name} />
          <div className="cb-cap">
            {runsShown} runs <small>{flt === "all" ? "" : `from ${RUN_LABEL.find((r) => r[0] === flt)?.[1]}`}{vs ? ` vs ${vs}` : ""}</small>
          </div>
        </div>
        <div className="rchips" role="group" aria-label="Filter by runs">
          {RUN_LABEL.map(([k, l]) => (
            <button key={k} type="button" aria-pressed={flt === k} disabled={k !== "all" && !count(k)} onClick={() => setFlt(k)}>
              {k === "all" ? null : <i style={{ background: RUN_COLOR[k] }} />}
              {l}
              {k === "all" ? "" : ` - ${count(k)}`}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

/** Spike / wagon-zone toggle plus one card per batter at the crease. */
const CurrentBatters = ({ batters, balls, live, loading, error }: Props) => {
  const [mode, setMode] = useState<"spike" | "zone">("spike");
  if (!batters.length) return <Empty>No batters at the crease right now.</Empty>;
  if (loading && !balls.length) return <Empty>Loading shot data…</Empty>;
  if (error && !balls.length) return <Empty>Could not load shot data. Try again later.</Empty>;
  const anyDir = balls.some((x) => !!x.sector);
  return (
    <div className="cbw">
      <div className="cbtog" role="group" aria-label="Graph type">
        <button type="button" aria-pressed={mode === "spike"} onClick={() => setMode("spike")}>
          Spike graph
        </button>
        <button type="button" aria-pressed={mode === "zone"} onClick={() => setMode("zone")}>
          Wagon zones
        </button>
      </div>
      {balls.length && !anyDir ? <div className="pa-note" style={{ marginTop: 10 }}>The scorer has not recorded shot directions for this innings, so the graphs stay empty.</div> : null}
      <div className="cbg">
        {batters.map((b) => (
          <Card key={b.id} b={b} all={balls} mode={mode} live={live} />
        ))}
      </div>
    </div>
  );
};

export default CurrentBatters;
